import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Edit3,
  Trash2,
  ChevronDown,
  ChevronUp,
  GripVertical,
  Play,
  FileText,
  Video,
  BookOpen,
  Clock,
  ArrowLeft,
  ExternalLink,
  Paperclip,
  MessageSquare,
  X,
  Upload,
} from "lucide-react";
import { toast } from "react-hot-toast";
import TiptapEditor from "../../components/common/TiptapEditor";
import api from "../../services/api";
import { eCourseService } from "../../services/elearning/eCourseService";
import { moduleService } from "../../services/elearning/moduleService";
import { contentService } from "../../services/elearning/contentService";
import { quizService } from "../../services/elearning/quizService";
import LoadingSpinner from "../../components/common/LoadingSpinner";

interface LearningModule {
  _id: string;
  title: string;
  description: string;
  order: number;
  estimatedDuration: number;
  status: "draft" | "published" | "archived";
  contents: CourseContent[];
  quizId?: string;
  quiz?: {
    _id: string;
    title: string;
    description: string;
    passingScore: number;
  };
}

interface CourseContent {
  _id: string;
  title: string;
  description: string;
  type:
    | "video"
    | "document"
    | "presentation"
    | "audio"
    | "interactive"
    | "text"
    | "mixed"
    | "link";
  order: number;
  estimatedDuration: number;
  status: "draft" | "published" | "archived";
  externalUrl?: string;
  mediaUrl?: string;
  // New array fields for multiple URLs and mixed content
  mediaUrls?: string[];
  externalUrls?: string[];
  contentItems?: ContentItem[];
  content?: string;
  materials?: Array<{
    id: string;
    name: string;
    type: "file" | "link";
    url?: string;
    fileUrl?: string;
    fileName?: string;
    fileSize?: number;
    mimeType?: string;
  }>;
}

interface ModuleFormData {
  title: string;
  description: string;
  estimatedDuration: number;
  status: "draft" | "published" | "archived" | "deleted";
  courseId?: string;
  quizId?: string;
}

interface ContentFormData {
  title: string;
  description: string;
  type:
    | "video"
    | "document"
    | "presentation"
    | "audio"
    | "interactive"
    | "text"
    | "link"
    | "mixed";
  content?: string;
  mediaUrls?: string[]; // Multiple media URLs
  externalUrls?: string[]; // Multiple external URLs
  estimatedDuration: number;
  tags?: string[];
  visibility?: "public" | "private" | "restricted";
  moduleId?: string;
  courseId?: string;
  status?: "draft" | "published" | "archived";
  files?: File[];
  // For mixed content type - multiple content items
  contentItems?: ContentItem[];
}

interface ContentItem {
  id: string;
  type:
    | "video"
    | "document"
    | "presentation"
    | "audio"
    | "interactive"
    | "text"
    | "link";
  title: string;
  content?: string;
  mediaUrl?: string;
  externalUrl?: string;
  files?: File[];
}

const CourseContentManagement: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [modules, setModules] = useState<LearningModule[]>([]);
  const [showModuleModal, setShowModuleModal] = useState(false);
  const [showContentModal, setShowContentModal] = useState(false);
  const [editingModule, setEditingModule] = useState<LearningModule | null>(
    null
  );
  const [editingContent, setEditingContent] = useState<CourseContent | null>(
    null
  );
  const [selectedModuleId, setSelectedModuleId] = useState<string | null>(null);
  const [expandedModules, setExpandedModules] = useState<Set<string>>(
    new Set()
  );

  const [moduleFormData, setModuleFormData] = useState<ModuleFormData>({
    title: "",
    description: "",
    estimatedDuration: 30,
    status: "draft",
  });

  const [contentFormData, setContentFormData] = useState<ContentFormData>({
    title: "",
    description: "",
    type: "text",
    content: "",
    estimatedDuration: 10,
    status: "draft",
    files: [],
    mediaUrls: [],
    externalUrls: [],
    contentItems: [],
  });

  // Debug: Add global drag event listener
  useEffect(() => {
    const handleGlobalDragEnter = (e: DragEvent) => {
      console.log("🔵 GLOBAL dragenter detected", e.target);
    };
    const handleGlobalDragOver = (e: DragEvent) => {
      console.log("🟢 GLOBAL dragover detected", e.target);
      e.preventDefault();
    };
    const handleGlobalDrop = (e: DragEvent) => {
      console.log("🔴 GLOBAL drop detected", e.target);
    };

    window.addEventListener("dragenter", handleGlobalDragEnter);
    window.addEventListener("dragover", handleGlobalDragOver);
    window.addEventListener("drop", handleGlobalDrop);

    return () => {
      window.removeEventListener("dragenter", handleGlobalDragEnter);
      window.removeEventListener("dragover", handleGlobalDragOver);
      window.removeEventListener("drop", handleGlobalDrop);
    };
  }, []);

  // Fetch course details (now includes modules)
  const { data: courseData, isLoading: courseLoading } = useQuery({
    queryKey: ["course-detail", courseId],
    queryFn: () => eCourseService.getCourseById(courseId!),
    enabled: !!courseId,
  });

  // Fetch quizzes for the course
  const {
    data: quizzesData,
    error: quizzesError,
    isLoading: quizzesLoading,
  } = useQuery({
    queryKey: ["course-quizzes", courseId],
    queryFn: async () => {
      try {
        const result = await quizService.getQuizzes({ courseId });
        console.log("Quiz service result:", result);
        return result;
      } catch (error) {
        console.error("Quiz service error:", error);
        throw error;
      }
    },
    enabled: !!courseId,
    retry: false, // Don't retry on error
  });

  // Debug: Log quiz data
  useEffect(() => {
    if (quizzesData) {
      console.log("Quizzes data:", quizzesData);
    }
    if (quizzesError) {
      console.error("Quizzes error:", quizzesError);
    }
  }, [quizzesData, quizzesError]);

  // Remove separate modules query since modules are now in course data

  useEffect(() => {
    if (courseData?.data?.modules) {
      // Transform the modules to match our interface
      const transformedModules = courseData.data.modules.map((module: any) => ({
        ...module,
        contents: Array.isArray(module.contents)
          ? module.contents.filter(
              (item: any) => typeof item === "object" && item._id
            )
          : [],
      }));
      setModules(transformedModules);
    }
  }, [courseData]);

  // Create module mutation
  const createModuleMutation = useMutation({
    mutationFn: (data: ModuleFormData) =>
      moduleService.createModule({
        title: data.title,
        description: data.description,
        estimatedDuration: data.estimatedDuration,
        status: data.status,
        courseId: courseId!,
      } as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["course-detail", courseId] });
      setShowModuleModal(false);
      resetModuleForm();
      toast.success("Module created successfully");
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || "Failed to create module");
    },
  });

  // Update module mutation
  const updateModuleMutation = useMutation({
    mutationFn: ({
      moduleId,
      data,
    }: {
      moduleId: string;
      data: Partial<ModuleFormData>;
    }) => moduleService.updateModule(courseId!, moduleId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["course-detail", courseId] });
      setShowModuleModal(false);
      setEditingModule(null);
      resetModuleForm();
      toast.success("Module updated successfully");
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || "Failed to update module");
    },
  });

  // Delete module mutation
  const deleteModuleMutation = useMutation({
    mutationFn: (moduleId: string) =>
      moduleService.deleteModule(courseId!, moduleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["course-detail", courseId] });
      toast.success("Module deleted successfully");
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || "Failed to delete module");
    },
  });

  // Create content mutation
  const createContentMutation = useMutation({
    mutationFn: async (data: ContentFormData & { moduleId: string }) => {
      // Create content with files (backend will handle file uploads)
      return contentService.createContent(
        {
          title: data.title,
          description: data.description,
          type: data.type,
          content: data.content,
          mediaUrls: data.mediaUrls,
          externalUrls: data.externalUrls,
          contentItems: data.contentItems,
          estimatedDuration: data.estimatedDuration,
          moduleId: data.moduleId,
        } as any,
        courseId!,
        data.files
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["course-detail", courseId] });
      setShowContentModal(false);
      resetContentForm();
      toast.success("Content created successfully");
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || "Failed to create content");
    },
  });

  // Update content mutation
  const updateContentMutation = useMutation({
    mutationFn: async ({
      contentId,
      data,
    }: {
      contentId: string;
      data: Partial<ContentFormData>;
    }) => {
      // If there are files to upload, use FormData
      if (data.files && data.files.length > 0) {
        const formData = new FormData();

        // Add basic fields
        formData.append("title", data.title || "");
        formData.append("description", data.description || "");
        formData.append("type", data.type || "document");
        formData.append(
          "estimatedDuration",
          String(data.estimatedDuration || 0)
        );
        formData.append("status", "published");

        // Add content-specific fields
        if (data.content) formData.append("content", data.content);
        if (data.mediaUrl) formData.append("mediaUrl", data.mediaUrl);
        if (data.externalUrl) formData.append("externalUrl", data.externalUrl);

        // Add existing materials if any
        if (editingContent?.materials) {
          formData.append(
            "materials",
            JSON.stringify(editingContent.materials)
          );
        }

        // Add files
        data.files.forEach((file, index) => {
          formData.append("files", file);
        });

        const response = await api.put(
          `/elearning/courses/${courseId}/content/${contentId}`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        return response;
      } else {
        // No files, use regular JSON update - pass courseId to use correct endpoint
        return contentService.updateContent(
          contentId,
          {
            title: data.title,
            description: data.description,
            type: data.type,
            content: data.content,
            mediaUrls: data.mediaUrls,
            externalUrls: data.externalUrls,
            contentItems: data.contentItems,
            estimatedDuration: data.estimatedDuration,
            materials: editingContent?.materials,
          } as any,
          courseId // Pass courseId to use the correct endpoint
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["course-detail", courseId] });
      setShowContentModal(false);
      setEditingContent(null);
      resetContentForm();
      toast.success("Content updated successfully");
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || "Failed to update content");
    },
  });

  // Delete content mutation
  const deleteContentMutation = useMutation({
    mutationFn: (contentId: string) => contentService.deleteContent(contentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["course-detail", courseId] });
      toast.success("Content deleted successfully");
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || "Failed to delete content");
    },
  });

  const resetModuleForm = () => {
    setModuleFormData({
      title: "",
      description: "",
      estimatedDuration: 30,
      status: "draft",
      quizId: undefined,
    });
  };

  const resetContentForm = () => {
    setContentFormData({
      title: "",
      description: "",
      type: "text",
      content: "",
      mediaUrls: [],
      externalUrls: [],
      contentItems: [],
      estimatedDuration: 10,
      status: "draft",
      files: [],
    });
  };

  const handleCreateModule = () => {
    if (!moduleFormData.title.trim()) {
      toast.error("Module title is required");
      return;
    }
    createModuleMutation.mutate(moduleFormData);
  };

  const handleUpdateModule = () => {
    if (!editingModule || !moduleFormData.title.trim()) {
      toast.error("Module title is required");
      return;
    }
    updateModuleMutation.mutate({
      moduleId: editingModule._id,
      data: moduleFormData,
    });
  };

  const handleEditModule = (module: LearningModule) => {
    setEditingModule(module);
    setModuleFormData({
      title: module.title,
      description: module.description,
      estimatedDuration: module.estimatedDuration,
      status: module.status,
      quizId: module.quizId,
    });
    setShowModuleModal(true);
  };

  const handleDeleteModule = (moduleId: string) => {
    if (
      confirm(
        "Are you sure you want to delete this module? This action cannot be undone."
      )
    ) {
      deleteModuleMutation.mutate(moduleId);
    }
  };

  const handleCreateContent = async () => {
    if (!contentFormData.title.trim() || !selectedModuleId) {
      toast.error("Content title and module selection are required");
      return;
    }

    // Validate required fields based on content type
    if (
      contentFormData.type === "video" &&
      (!contentFormData.mediaUrls || contentFormData.mediaUrls.length === 0) &&
      (!contentFormData.files || contentFormData.files.length === 0)
    ) {
      toast.error(
        "At least one media URL is required for video content (or upload a video file)"
      );
      return;
    }

    if (
      contentFormData.type === "link" &&
      (!contentFormData.externalUrls ||
        contentFormData.externalUrls.length === 0)
    ) {
      toast.error(
        "At least one external link URL is required for link content"
      );
      return;
    }

    if (
      contentFormData.type === "mixed" &&
      (!contentFormData.contentItems ||
        contentFormData.contentItems.length === 0)
    ) {
      toast.error("At least one content item is required for mixed content");
      return;
    }

    if (contentFormData.type === "text" && !contentFormData.content?.trim()) {
      toast.error("Content text is required for text content");
      return;
    }

    // Create content (backend will handle file uploads)
    createContentMutation.mutate({
      ...contentFormData,
      moduleId: selectedModuleId,
    });
  };

  const handleUpdateContent = () => {
    if (!editingContent || !contentFormData.title.trim()) {
      toast.error("Content title is required");
      return;
    }
    updateContentMutation.mutate({
      contentId: editingContent._id,
      data: contentFormData,
    });
  };

  const handleEditContent = (content: CourseContent, moduleId: string) => {
    setEditingContent(content);
    setSelectedModuleId(moduleId);
    setContentFormData({
      title: content.title,
      description: content.description,
      type: content.type,
      content: content.content || "",
      mediaUrls:
        content.mediaUrls || (content.mediaUrl ? [content.mediaUrl] : []),
      externalUrls:
        content.externalUrls ||
        (content.externalUrl ? [content.externalUrl] : []),
      contentItems: content.contentItems || [],
      estimatedDuration: content.estimatedDuration,
      status: content.status,
      files: [], // Ensure files array is always initialized
    });
    setShowContentModal(true);
  };

  const handleDeleteContent = (contentId: string) => {
    if (
      confirm(
        "Are you sure you want to delete this content? This action cannot be undone."
      )
    ) {
      deleteContentMutation.mutate(contentId);
    }
  };

  const handleDeleteMaterial = async (
    contentId: string,
    materialId: string | number
  ) => {
    try {
      console.log("Deleting material:", { contentId, materialId, courseId });
      // Call API to delete the specific material
      await api.delete(
        `/elearning/content/${contentId}/materials/${materialId}`
      );
      toast.success("Material deleted successfully");
      console.log("Material deleted, invalidating queries for:", [
        "course-detail",
        courseId,
      ]);
      // Refresh the content data
      queryClient.invalidateQueries({ queryKey: ["course-detail", courseId] });
      console.log("Query invalidated");
    } catch (error: any) {
      console.error("Error deleting material:", error);
      toast.error(
        error?.response?.data?.message || "Failed to delete material"
      );
    }
  };

  const toggleModuleExpansion = (moduleId: string) => {
    const newExpanded = new Set(expandedModules);
    if (newExpanded.has(moduleId)) {
      newExpanded.delete(moduleId);
    } else {
      newExpanded.add(moduleId);
    }
    setExpandedModules(newExpanded);
  };

  const getContentIcon = (type: string) => {
    switch (type) {
      case "video":
        return <Play className="w-4 h-4" />;
      case "document":
        return <FileText className="w-4 h-4" />;
      case "presentation":
        return <FileText className="w-4 h-4" />;
      case "audio":
        return <Video className="w-4 h-4" />;
      case "interactive":
        return <BookOpen className="w-4 h-4" />;
      case "text":
        return <FileText className="w-4 h-4" />;
      case "link":
        return <ExternalLink className="w-4 h-4" />;
      case "mixed":
        return <BookOpen className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  if (courseLoading) {
    return <LoadingSpinner />;
  }

  const course = courseData?.data;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <div className="bg-white/95 backdrop-blur-xl border-b border-slate-200/60 shadow-lg">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 text-slate-600 hover:text-slate-900 font-semibold transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                Back
              </button>
              <div className="h-6 w-px bg-slate-300"></div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">
                  Course Content Management
                </h1>
                {course && (
                  <p className="text-slate-600 font-medium">{course.title}</p>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() =>
                  navigate(`/elearning/courses/${courseId}/discussions`)
                }
                className="flex items-center gap-2 px-5 py-2.5 bg-white border-2 border-slate-200 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300"
              >
                <MessageSquare className="w-4 h-4 text-emerald-500" />
                <span className="text-slate-700">Discussions</span>
              </button>
              <button
                onClick={() => {
                  setEditingModule(null);
                  resetModuleForm();
                  setShowModuleModal(true);
                }}
                className="flex items-center gap-2 px-5 py-2.5 bg-white border-2 border-slate-200 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300"
              >
                <Plus className="w-4 h-4 text-blue-500" />
                <span className="text-slate-700">Add Module</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {modules.length === 0 ? (
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-12 text-center">
            <div className="bg-gradient-to-br from-blue-300 to-indigo-400 rounded-2xl p-4 w-16 h-16 mx-auto shadow-lg flex items-center justify-center mb-4">
              <BookOpen className="text-white" size={32} />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">
              No modules yet
            </h3>
            <p className="text-slate-600 font-medium mb-6">
              Start building your course by creating modules and adding content.
            </p>
            <button
              onClick={() => {
                setEditingModule(null);
                resetModuleForm();
                setShowModuleModal(true);
              }}
              className="px-6 py-3 bg-white border-2 border-slate-200 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300 inline-flex items-center gap-2"
            >
              <Plus className="w-5 h-5 text-blue-500" />
              <span className="text-slate-700">Create Your First Module</span>
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {modules.map((module) => (
              <div
                key={module._id}
                className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300"
              >
                {/* Module Header */}
                <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-blue-50">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <GripVertical className="w-5 h-5 text-slate-400 cursor-move flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-bold text-slate-900 truncate">
                          {module.title}
                        </h3>
                        <p className="text-slate-600 text-sm font-medium truncate">
                          {module.description}
                        </p>
                        <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-slate-600 font-medium">
                          <span className="flex items-center gap-1.5">
                            <Clock className="w-4 h-4 text-amber-500" />
                            {module.estimatedDuration} min
                          </span>
                          <span className="flex items-center gap-1.5">
                            <BookOpen className="w-4 h-4 text-blue-500" />
                            {module.contents?.length || 0} lessons
                          </span>
                          {module.quiz && (
                            <span className="flex items-center gap-1.5">
                              <FileText className="w-4 h-4 text-purple-500" />
                              Quiz: {module.quiz.title} (
                              {module.quiz.passingScore}%)
                            </span>
                          )}
                          <span
                            className={`px-3 py-1 rounded-xl text-xs font-bold shadow-sm ${
                              module.status === "published"
                                ? "bg-gradient-to-br from-emerald-300 to-emerald-400 text-white"
                                : "bg-gradient-to-br from-amber-300 to-yellow-400 text-white"
                            }`}
                          >
                            {module.status}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => toggleModuleExpansion(module._id)}
                        className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
                      >
                        {expandedModules.has(module._id) ? (
                          <ChevronUp className="w-5 h-5 text-slate-600" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-slate-600" />
                        )}
                      </button>
                      <button
                        onClick={() => handleEditModule(module)}
                        className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
                      >
                        <Edit3 className="w-5 h-5 text-amber-500" />
                      </button>
                      <button
                        onClick={() => handleDeleteModule(module._id)}
                        className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
                      >
                        <Trash2 className="w-5 h-5 text-red-500" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Module Content */}
                {expandedModules.has(module._id) && (
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-bold text-slate-900">Lessons</h4>
                      <button
                        onClick={() => {
                          setSelectedModuleId(module._id);
                          setEditingContent(null);
                          resetContentForm();
                          setShowContentModal(true);
                        }}
                        className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-slate-200 text-sm rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300"
                      >
                        <Plus className="w-4 h-4 text-emerald-500" />
                        <span className="text-slate-700">Add Lesson</span>
                      </button>
                    </div>

                    {module.contents && module.contents.length > 0 ? (
                      <div className="space-y-3">
                        {module.contents.map((content) => (
                          <div
                            key={content._id}
                            className="flex items-center justify-between p-4 bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl border border-slate-200 hover:shadow-md transition-all duration-300"
                          >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              {getContentIcon(content.type)}
                              <div className="flex-1 min-w-0">
                                <h5 className="font-bold text-slate-900 truncate">
                                  {content.title}
                                </h5>
                                <p className="text-sm text-slate-600 font-medium truncate">
                                  {content.description}
                                </p>
                                <div className="flex flex-wrap items-center gap-3 mt-1.5 text-xs text-slate-600 font-medium">
                                  <span className="capitalize">
                                    {content.type}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3 h-3 text-amber-500" />
                                    {content.estimatedDuration} min
                                  </span>
                                  <span
                                    className={`px-2 py-1 rounded-lg font-bold ${
                                      content.status === "published"
                                        ? "bg-gradient-to-br from-emerald-300 to-emerald-400 text-white"
                                        : "bg-gradient-to-br from-amber-300 to-yellow-400 text-white"
                                    }`}
                                  >
                                    {content.status}
                                  </span>
                                </div>
                                {/* Display external URL for video/link content */}
                                {(content.externalUrl || content.mediaUrl) && (
                                  <div className="mt-2 text-xs">
                                    <a
                                      href={
                                        content.externalUrl || content.mediaUrl
                                      }
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-blue-600 hover:text-blue-800 font-semibold flex items-center gap-1"
                                    >
                                      <ExternalLink className="w-3 h-3" />
                                      {content.type === "video"
                                        ? "Watch Video"
                                        : "Open Link"}
                                    </a>
                                  </div>
                                )}
                                {/* Display materials/attachments */}
                                {content.materials &&
                                  content.materials.length > 0 && (
                                    <div className="mt-2 text-xs text-slate-600 font-medium">
                                      <div className="flex items-center gap-1 mb-1">
                                        <Paperclip className="w-3 h-3 text-purple-500" />
                                        <span>
                                          {content.materials.length} attachment
                                          {content.materials.length !== 1
                                            ? "s"
                                            : ""}
                                        </span>
                                      </div>
                                      <div className="space-y-1">
                                        {content.materials.map(
                                          (material: any, index: number) => (
                                            <div
                                              key={material.id || index}
                                              className="flex items-center justify-between bg-white p-2 rounded-lg border border-slate-200"
                                            >
                                              <a
                                                href={
                                                  material.fileUrl ||
                                                  material.url
                                                }
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-600 hover:text-blue-800 font-semibold flex-1 truncate mr-2"
                                              >
                                                {material.fileName ||
                                                  material.name}
                                              </a>
                                              <div className="flex gap-1">
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    // Handle material edit - could open a modal or inline edit
                                                    toast.success(
                                                      "Material editing feature coming soon"
                                                    );
                                                  }}
                                                  className="p-1 rounded hover:bg-slate-100 transition-colors"
                                                  title="Edit material"
                                                >
                                                  <Edit3 className="w-3 h-3 text-amber-500" />
                                                </button>
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (
                                                      confirm(
                                                        `Delete material "${
                                                          material.fileName ||
                                                          material.name
                                                        }"?`
                                                      )
                                                    ) {
                                                      // Handle material deletion
                                                      handleDeleteMaterial(
                                                        content._id,
                                                        material.id || index
                                                      );
                                                    }
                                                  }}
                                                  className="p-1 rounded hover:bg-slate-100 transition-colors"
                                                  title="Delete material"
                                                >
                                                  <Trash2 className="w-3 h-3 text-red-500" />
                                                </button>
                                              </div>
                                            </div>
                                          )
                                        )}
                                      </div>
                                    </div>
                                  )}
                                {/* Display text content preview */}
                                {content.type === "text" && content.content && (
                                  <div className="mt-2 text-xs text-slate-600 font-medium">
                                    <div className="bg-white p-2 rounded-lg border border-slate-200 max-w-md truncate">
                                      {content.content}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <button
                                onClick={() =>
                                  handleEditContent(content, module._id)
                                }
                                className="p-2 rounded-lg hover:bg-white transition-colors"
                              >
                                <Edit3 className="w-4 h-4 text-amber-500" />
                              </button>
                              <button
                                onClick={() => handleDeleteContent(content._id)}
                                className="p-2 rounded-lg hover:bg-white transition-colors"
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl border border-slate-200">
                        <div className="bg-gradient-to-br from-slate-300 to-slate-400 rounded-xl p-3 w-12 h-12 mx-auto shadow-lg flex items-center justify-center mb-3">
                          <BookOpen className="w-6 h-6 text-white" />
                        </div>
                        <p className="text-slate-600 font-medium">
                          No lessons in this module yet
                        </p>
                        <button
                          onClick={() => {
                            setSelectedModuleId(module._id);
                            setEditingContent(null);
                            resetContentForm();
                            setShowContentModal(true);
                          }}
                          className="mt-3 text-blue-600 hover:text-blue-700 text-sm font-bold"
                        >
                          Add your first lesson
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Module Modal */}
      {showModuleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
            <h3 className="text-lg font-semibold mb-4">
              {editingModule ? "Edit Module" : "Create Module"}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title *
                </label>
                <input
                  type="text"
                  value={moduleFormData.title}
                  onChange={(e) =>
                    setModuleFormData({
                      ...moduleFormData,
                      title: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Module title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={moduleFormData.description}
                  onChange={(e) =>
                    setModuleFormData({
                      ...moduleFormData,
                      description: e.target.value,
                    })
                  }
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Module description"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Estimated Duration (minutes)
                </label>
                <input
                  type="number"
                  value={moduleFormData.estimatedDuration || ""}
                  placeholder="0"
                  onChange={(e) =>
                    setModuleFormData({
                      ...moduleFormData,
                      estimatedDuration:
                        e.target.value === ""
                          ? ""
                          : parseInt(e.target.value) || 0,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={moduleFormData.status}
                  onChange={(e) =>
                    setModuleFormData({
                      ...moduleFormData,
                      status: e.target.value as
                        | "draft"
                        | "published"
                        | "archived",
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Attached Quiz (Optional)
                </label>
                <select
                  value={moduleFormData.quizId || ""}
                  onChange={(e) =>
                    setModuleFormData({
                      ...moduleFormData,
                      quizId: e.target.value || undefined,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">No quiz attached</option>
                  {quizzesData?.quizzes?.map((quiz: any) => (
                    <option key={quiz._id} value={quiz._id}>
                      {quiz.title} (Passing: {quiz.passingScore}%)
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Students must complete this quiz to unlock the next module
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowModuleModal(false);
                  setEditingModule(null);
                  resetModuleForm();
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={
                  editingModule ? handleUpdateModule : handleCreateModule
                }
                disabled={
                  createModuleMutation.isPending ||
                  updateModuleMutation.isPending
                }
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {createModuleMutation.isPending ||
                updateModuleMutation.isPending ? (
                  <LoadingSpinner />
                ) : editingModule ? (
                  "Update"
                ) : (
                  "Create"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content Modal */}
      {showContentModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onDragEnter={(e) => {
            console.log("Drag entered modal backdrop");
            e.preventDefault();
          }}
          onDragOver={(e) => {
            console.log("Drag over modal backdrop");
            e.preventDefault();
          }}
          onDrop={(e) => {
            console.log("Drop on modal backdrop");
            e.preventDefault();
          }}
        >
          <div
            className="bg-white rounded-lg p-6 w-full max-w-2xl relative overflow-y-auto max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
            onDragEnter={(e) => {
              console.log("Drag entered modal content");
              e.stopPropagation();
            }}
            onDragOver={(e) => {
              console.log("Drag over modal content");
              e.preventDefault();
              e.stopPropagation();
            }}
            onDrop={(e) => {
              console.log("Drop on modal content");
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            {/* Close button in top right */}
            <button
              onClick={() => {
                setShowContentModal(false);
                setEditingContent(null);
                resetContentForm();
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
            <h3 className="text-lg font-semibold mb-4">
              {editingContent ? "Edit Lesson" : "Create Lesson"}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title *
                </label>
                <input
                  type="text"
                  value={contentFormData.title}
                  onChange={(e) =>
                    setContentFormData({
                      ...contentFormData,
                      title: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Lesson title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description *
                </label>
                <textarea
                  required
                  value={contentFormData.description}
                  onChange={(e) =>
                    setContentFormData({
                      ...contentFormData,
                      description: e.target.value,
                    })
                  }
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Lesson description"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Content Type
                </label>
                <select
                  value={contentFormData.type}
                  onChange={(e) =>
                    setContentFormData({
                      ...contentFormData,
                      type: e.target.value as any,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="text">Text</option>
                  <option value="video">Video</option>
                  <option value="document">Document</option>
                  <option value="presentation">Presentation</option>
                  <option value="audio">Audio</option>
                  <option value="link">External Link</option>
                  <option value="interactive">Interactive</option>
                  <option value="mixed">Mixed</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Estimated Duration (minutes)
                </label>
                <input
                  type="number"
                  value={contentFormData.estimatedDuration || ""}
                  placeholder="0"
                  onChange={(e) =>
                    setContentFormData({
                      ...contentFormData,
                      estimatedDuration:
                        e.target.value === ""
                          ? ""
                          : parseInt(e.target.value) || 0,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={contentFormData.status}
                  onChange={(e) =>
                    setContentFormData({
                      ...contentFormData,
                      status: e.target.value as
                        | "draft"
                        | "published"
                        | "archived",
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Attachments
                </label>
                {/* Drag and Drop Area - Pure Native Implementation */}
                <div>
                  {/* Simple file input */}
                  <input
                    type="file"
                    multiple
                    accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.ppt,.pptx"
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      console.log("Files selected via input:", files);
                      if (files.length > 0) {
                        setContentFormData((prevData) => ({
                          ...prevData,
                          files: [...(prevData.files || []), ...files],
                        }));
                        files.forEach((file) => {
                          toast.success(`Added ${file.name}`);
                        });
                      }
                      // Reset input
                      e.target.value = "";
                    }}
                    className="hidden"
                    id="file-upload-input"
                  />
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      document.getElementById("file-upload-input")?.click();
                    }}
                    onDragEnter={(e) => {
                      console.log("✅ dragenter");
                      e.preventDefault();
                      e.stopPropagation();
                      e.currentTarget.classList.add(
                        "border-blue-500",
                        "bg-blue-50"
                      );
                      e.currentTarget.classList.remove("border-gray-300");
                    }}
                    onDragOver={(e) => {
                      console.log("✅ dragover");
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onDragLeave={(e) => {
                      console.log("✅ dragleave");
                      e.preventDefault();
                      e.stopPropagation();
                      e.currentTarget.classList.remove(
                        "border-blue-500",
                        "bg-blue-50"
                      );
                      e.currentTarget.classList.add("border-gray-300");
                    }}
                    onDrop={(e) => {
                      console.log("✅ DROP EVENT!");
                      e.preventDefault();
                      e.stopPropagation();

                      e.currentTarget.classList.remove(
                        "border-blue-500",
                        "bg-blue-50"
                      );
                      e.currentTarget.classList.add("border-gray-300");

                      const files = Array.from(e.dataTransfer?.files || []);
                      console.log("Files from drop:", files);
                      if (files.length > 0) {
                        setContentFormData((prevData) => ({
                          ...prevData,
                          files: [...(prevData.files || []), ...files],
                        }));
                        files.forEach((file) => {
                          toast.success(`Added ${file.name}`);
                        });
                      }
                    }}
                    className="relative z-10 border-2 border-dashed border-gray-300 rounded-lg p-6 text-center transition-colors cursor-pointer hover:border-blue-400"
                    style={{ minHeight: 120 }}
                  >
                    <div className="space-y-2 pointer-events-none">
                      <Upload className="mx-auto h-8 w-8 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          Drop files here or click to browse
                        </p>
                        <p className="text-xs text-gray-500">
                          Images, videos, audio, PDFs, and documents
                        </p>
                      </div>
                    </div>
                  </div>
                </div>{" "}
                {/* Display selected files */}
                {contentFormData.files && contentFormData.files.length > 0 && (
                  <div className="mt-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium text-gray-700">
                        Selected Files ({contentFormData.files.length}):
                      </p>
                      <button
                        type="button"
                        onClick={() =>
                          setContentFormData({
                            ...contentFormData,
                            files: [],
                          })
                        }
                        className="text-xs text-red-600 hover:text-red-800"
                      >
                        Clear all
                      </button>
                    </div>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {contentFormData.files.map((file, index) => (
                        <div
                          key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
                          className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                        >
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <FileText className="h-4 w-4 text-gray-500 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {file.name}
                              </p>
                              <p className="text-xs text-gray-500">
                                {(file.size / 1024 / 1024).toFixed(2)} MB
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setContentFormData({
                                ...contentFormData,
                                files: contentFormData.files?.filter(
                                  (_, i) => i !== index
                                ),
                              });
                            }}
                            className="ml-2 p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                            title="Remove file"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Conditional fields based on content type */}
              {contentFormData.type === "text" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Content
                  </label>
                  <div className="content-editor rounded-lg overflow-hidden">
                    <TiptapEditor
                      value={contentFormData.content || ""}
                      onChange={(value) =>
                        setContentFormData({
                          ...contentFormData,
                          content: value,
                        })
                      }
                      placeholder="Enter your lesson content here... Use the toolbar to format text, add images, videos, and more."
                    />
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    💡 Use the toolbar to format text, add headings, lists,
                    images, videos, code blocks, and more.
                  </p>
                </div>
              )}

              {(contentFormData.type === "video" ||
                contentFormData.type === "audio") && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Media URLs
                  </label>
                  {contentFormData.mediaUrls.map((url, index) => (
                    <div key={index} className="flex gap-2 mb-2">
                      <input
                        type="url"
                        value={url}
                        onChange={(e) => {
                          const newUrls = [...contentFormData.mediaUrls];
                          newUrls[index] = e.target.value;
                          setContentFormData({
                            ...contentFormData,
                            mediaUrls: newUrls,
                          });
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="https://example.com/video.mp4 or YouTube URL"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const newUrls = contentFormData.mediaUrls.filter(
                            (_, i) => i !== index
                          );
                          setContentFormData({
                            ...contentFormData,
                            mediaUrls: newUrls,
                          });
                        }}
                        className="px-3 py-2 text-red-600 hover:text-red-800"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      setContentFormData({
                        ...contentFormData,
                        mediaUrls: [...contentFormData.mediaUrls, ""],
                      });
                    }}
                    className="px-3 py-2 text-blue-600 hover:text-blue-800 text-sm"
                  >
                    + Add Media URL
                  </button>
                  <p className="text-xs text-gray-500 mt-1">
                    Enter direct media URLs or YouTube/Vimeo links. You can add
                    multiple URLs.
                  </p>
                </div>
              )}

              {contentFormData.type === "link" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    External Links
                  </label>
                  {contentFormData.externalUrls.map((url, index) => (
                    <div key={index} className="flex gap-2 mb-2">
                      <input
                        type="url"
                        value={url}
                        onChange={(e) => {
                          const newUrls = [...contentFormData.externalUrls];
                          newUrls[index] = e.target.value;
                          setContentFormData({
                            ...contentFormData,
                            externalUrls: newUrls,
                          });
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="https://example.com/resource"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const newUrls = contentFormData.externalUrls.filter(
                            (_, i) => i !== index
                          );
                          setContentFormData({
                            ...contentFormData,
                            externalUrls: newUrls,
                          });
                        }}
                        className="px-3 py-2 text-red-600 hover:text-red-800"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      setContentFormData({
                        ...contentFormData,
                        externalUrls: [...contentFormData.externalUrls, ""],
                      });
                    }}
                    className="px-3 py-2 text-blue-600 hover:text-blue-800 text-sm"
                  >
                    + Add External Link
                  </button>
                  <p className="text-xs text-gray-500 mt-1">
                    Enter URLs of external resources. You can add multiple
                    links.
                  </p>
                </div>
              )}

              {contentFormData.type === "mixed" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Content Items
                  </label>

                  {/* Display existing content items */}
                  {contentFormData.contentItems.map((item, index) => (
                    <div
                      key={item.id}
                      className="border border-gray-200 rounded-lg p-4 mb-4"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-gray-900">
                          Item {index + 1}: {item.title || "Untitled"}
                        </h4>
                        <button
                          type="button"
                          onClick={() => {
                            const newItems =
                              contentFormData.contentItems.filter(
                                (_, i) => i !== index
                              );
                            setContentFormData({
                              ...contentFormData,
                              contentItems: newItems,
                            });
                          }}
                          className="text-red-600 hover:text-red-800"
                        >
                          Remove
                        </button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Type
                          </label>
                          <select
                            value={item.type}
                            onChange={(e) => {
                              const newItems = [
                                ...contentFormData.contentItems,
                              ];
                              newItems[index] = {
                                ...newItems[index],
                                type: e.target.value as ContentItem["type"],
                              };
                              setContentFormData({
                                ...contentFormData,
                                contentItems: newItems,
                              });
                            }}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                          >
                            <option value="text">Text</option>
                            <option value="video">Video</option>
                            <option value="document">Document</option>
                            <option value="presentation">Presentation</option>
                            <option value="audio">Audio</option>
                            <option value="link">External Link</option>
                            <option value="interactive">Interactive</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Title
                          </label>
                          <input
                            type="text"
                            value={item.title}
                            onChange={(e) => {
                              const newItems = [
                                ...contentFormData.contentItems,
                              ];
                              newItems[index] = {
                                ...newItems[index],
                                title: e.target.value,
                              };
                              setContentFormData({
                                ...contentFormData,
                                contentItems: newItems,
                              });
                            }}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            placeholder="Item title"
                          />
                        </div>
                      </div>

                      {/* Content-specific fields based on item type */}
                      {item.type === "text" && (
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Content
                          </label>
                          <div className="content-editor-compact rounded overflow-hidden">
                            <TiptapEditor
                              value={item.content || ""}
                              onChange={(value) => {
                                const newItems = [
                                  ...contentFormData.contentItems,
                                ];
                                newItems[index] = {
                                  ...newItems[index],
                                  content: value,
                                };
                                setContentFormData({
                                  ...contentFormData,
                                  contentItems: newItems,
                                });
                              }}
                              placeholder="Enter content..."
                            />
                          </div>
                        </div>
                      )}

                      {(item.type === "video" || item.type === "audio") && (
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Media URL
                          </label>
                          <input
                            type="url"
                            value={item.mediaUrl || ""}
                            onChange={(e) => {
                              const newItems = [
                                ...contentFormData.contentItems,
                              ];
                              newItems[index] = {
                                ...newItems[index],
                                mediaUrl: e.target.value,
                              };
                              setContentFormData({
                                ...contentFormData,
                                contentItems: newItems,
                              });
                            }}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            placeholder="https://example.com/media.mp4"
                          />
                        </div>
                      )}

                      {item.type === "link" && (
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            External URL
                          </label>
                          <input
                            type="url"
                            value={item.externalUrl || ""}
                            onChange={(e) => {
                              const newItems = [
                                ...contentFormData.contentItems,
                              ];
                              newItems[index] = {
                                ...newItems[index],
                                externalUrl: e.target.value,
                              };
                              setContentFormData({
                                ...contentFormData,
                                contentItems: newItems,
                              });
                            }}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            placeholder="https://example.com/resource"
                          />
                        </div>
                      )}

                      {(item.type === "document" ||
                        item.type === "presentation" ||
                        item.type === "interactive") && (
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            File Upload
                          </label>
                          <input
                            type="file"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                const newItems = [
                                  ...contentFormData.contentItems,
                                ];
                                newItems[index] = {
                                  ...newItems[index],
                                  files: [file],
                                };
                                setContentFormData({
                                  ...contentFormData,
                                  contentItems: newItems,
                                });
                              }
                            }}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            accept={
                              item.type === "document"
                                ? ".pdf,.doc,.docx,.txt"
                                : item.type === "presentation"
                                ? ".ppt,.pptx"
                                : ".html,.zip"
                            }
                          />
                          {item.files && item.files.length > 0 && (
                            <p className="text-xs text-gray-600 mt-1">
                              Selected: {item.files[0].name}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Add new content item button */}
                  <button
                    type="button"
                    onClick={() => {
                      const newItem: ContentItem = {
                        id: `item-${Date.now()}`,
                        type: "text",
                        title: "",
                      };
                      setContentFormData({
                        ...contentFormData,
                        contentItems: [
                          ...contentFormData.contentItems,
                          newItem,
                        ],
                      });
                    }}
                    className="w-full py-2 px-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-gray-400 hover:text-gray-800 transition-colors"
                  >
                    + Add Content Item
                  </button>

                  <p className="text-xs text-gray-500 mt-2">
                    Add multiple content items of different types to create a
                    mixed lesson.
                  </p>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={
                  editingContent ? handleUpdateContent : handleCreateContent
                }
                disabled={
                  createContentMutation.isPending ||
                  updateContentMutation.isPending
                }
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {createContentMutation.isPending ||
                updateContentMutation.isPending ? (
                  <LoadingSpinner />
                ) : editingContent ? (
                  "Update"
                ) : (
                  "Create"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseContentManagement;
